import React, { useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useQuery, useMutation, gql } from '@apollo/client'
import {
  Box,
  Button,
  IconButton,
  Modal,
  Switch,
  Tooltip,
  Typography
} from '@mui/material'
import AddRounded from '@mui/icons-material/AddRounded'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { getCoupons, deleteCoupon, editCoupon } from '../../apollo'
import CouponComponent from '../Coupon/Coupon'
import { ConfirmDialog } from '../UI/RowActionMenu'
import DataTable from '../DataTable'
import TableHeader from '../TableHeader'
import CustomLoader from '../Loader/CustomLoader'
import { customStyles } from '../../utils/tableCustomStyles'
import { modalOverlay, modalCard } from '../../utils/modalStyles'
import { raisedButtonSx } from '../UI/Depth'

const GET_COUPONS = gql`
  ${getCoupons}
`
const EDIT_COUPON = gql`
  ${editCoupon}
`
const DELETE_COUPON = gql`
  ${deleteCoupon}
`

// Free delivery vouchers: coupons that take a percentage off the delivery fee
// only (100% = free delivery). Handed out to groups such as university
// students, so they live beside the ordinary coupons rather than per store.
function FreeDelivery(props) {
  const { t } = props
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [voucher, setVoucher] = useState(null)
  const { data, loading } = useQuery(GET_COUPONS)
  const [mutateEdit] = useMutation(EDIT_COUPON)
  const [mutateDelete] = useMutation(DELETE_COUPON, {
    refetchQueries: [{ query: GET_COUPONS }]
  })

  const vouchers = (data?.coupons || []).filter(
    coupon => coupon.appliesTo === 'DELIVERY'
  )

  const openModal = row => {
    setVoucher(row || null)
    setModalOpen(true)
  }
  const closeModal = () => {
    setModalOpen(false)
    setVoucher(null)
  }

  const columns = [
    { name: t('Code'), sortable: true, selector: 'title' },
    {
      name: t('DeliveryFeeDiscount'),
      sortable: true,
      selector: 'discount',
      cell: row =>
        Number(row.discount) >= 100 ? (
          <Typography fontSize={13} fontWeight={600} color="success.main">
            {t('FreeDeliveryLabel')}
          </Typography>
        ) : (
          <>{`${row.discount}% ${t('OffDelivery')}`}</>
        )
    },
    {
      name: 'Max Coupon Limit',
      selector: 'maxCouponLimit',
      cell: row => <>{row.maxCouponLimit ?? '-'}</>
    },
    {
      name: 'Customer Usage Limit',
      selector: 'customerUsageLimit',
      cell: row => <>{row.customerUsageLimit ?? '-'}</>
    },
    {
      name: t('Status'),
      cell: row => (
        <Switch
          size="small"
          defaultChecked={row.enabled}
          onChange={() =>
            mutateEdit({
              variables: {
                couponInput: {
                  _id: row._id,
                  title: row.title,
                  discount: row.discount,
                  enabled: !row.enabled,
                  discountType: 'PERCENTAGE',
                  appliesTo: 'DELIVERY',
                  maxCouponLimit: row.maxCouponLimit ?? null,
                  customerUsageLimit: row.customerUsageLimit ?? null
                }
              }
            })
          }
          style={{ color: 'black' }}
        />
      )
    },
    {
      name: t('Action'),
      cell: row => (
        <Box display="flex">
          <Tooltip title={t('Edit')}>
            <IconButton size="small" onClick={() => openModal(row)}>
              <EditIcon fontSize="small" style={{ color: 'green' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('Delete')}>
            <IconButton size="small" onClick={() => setDeleteTarget(row)}>
              <DeleteIcon fontSize="small" style={{ color: 'red' }} />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ]

  return (
    <Box mt={4}>
      <DataTable
        title={<TableHeader title={t('FreeDeliveryVouchers')} />}
        subHeader={true}
        subHeaderComponent={
          <Button
            startIcon={<AddRounded />}
            onClick={() => openModal(null)}
            sx={raisedButtonSx}>
            {t('AddFreeDeliveryVoucher')}
          </Button>
        }
        columns={columns}
        data={vouchers}
        pagination
        progressPending={loading}
        progressComponent={<CustomLoader />}
        noDataComponent={
          <Box p={3}>
            <Typography fontSize={13} color="text.secondary">
              {t('NoFreeDeliveryVouchers')}
            </Typography>
          </Box>
        }
        customStyles={customStyles}
      />
      <Modal open={modalOpen} onClose={closeModal} sx={modalOverlay}>
        <Box sx={modalCard('md')}>
          <CouponComponent
            deliveryVoucher
            coupon={voucher}
            onClose={closeModal}
          />
        </Box>
      </Modal>
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`${t('Delete')}?`}
        message={t('DeleteConfirm')}
        confirmLabel={t('Delete')}
        cancelLabel={t('Cancel')}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          mutateDelete({ variables: { id: deleteTarget._id } })
          setDeleteTarget(null)
        }}
      />
    </Box>
  )
}

export default withTranslation()(FreeDelivery)
